import React from 'react';
import { ArrowRightIcon, LockIcon } from 'lucide-react';
import { InitialAvatar } from '../components/InitialAvatar';
import { Button } from '../components/ui/Button';
import { PANEL_PHOTOS, WORDMARK } from '../data/registration';
import { ERA_STORIES } from '../data/registration';
interface InviteLandingProps {
  onStart: () => void;
  /** The person who sent the invitation, as first name + last initial. */
  from?: string;
  fromYears?: string;
  aroundYear?: string;
}

/**
 * What a personally invited person sees FIRST — who thought of them, and when
 * they were both here. No form on this page at all.
 */
export function InviteLanding({
  onStart,
  from = 'Ray M.',
  fromYears = '1994–1999',
  aroundYear = '1996'
}: InviteLandingProps) {
  const era = ERA_STORIES['1990s'];
  return <div className="flex flex-col" data-id="element-971" data-mp="f6ylz5aj5aio">
      <section className="border-b border-rule" data-id="element-972" data-mp="f80b2dodrxje">
        <div className="mx-auto grid w-full max-w-5xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-center lg:py-16" data-id="element-973" data-mp="b66ccaxqx3lv">
          <div className="flex max-w-xl flex-col gap-5" data-id="element-974" data-mp="b1ksyuzidg43">
            <span className="flex items-center gap-3" data-id="element-975" data-mp="fz35swgncfhd">
              <InitialAvatar name={from} size={48} data-id="element-976" data-mp="fzmedmkepriq" />
              <span className="flex flex-col" data-id="element-977" data-mp="kxxmnsv1ztyu">
                <span className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-978" data-mp="kx4qfltq0muy">
                  An invitation from
                </span>
                <span className="text-body font-medium text-ink" data-id="element-979" data-mp="up0nqunzg8y4">
                  {from} · {fromYears}
                </span>
              </span>
            </span>

            <h1 className="text-page-title font-semibold text-ink sm:text-display" data-id="element-980" data-mp="yrovt04vhbu1">
              {from} thinks you were here around the same time.
            </h1>

            <p className="text-body text-ink-muted" data-id="element-981" data-mp="18ni9wny8pddx">
              They were at {WORDMARK} {fromYears}
              {aroundYear ? `, and remembered you from around ${aroundYear}` : ''}. The portal is
              a members&apos; archive and directory for everyone connected to this place,
              people who were in a program, worked here, volunteered, gave, or are family and
              friends.
            </p>

            <div className="flex flex-col gap-3" data-id="element-982" data-mp="2yfghnbyrjduz">
              <div className="flex flex-wrap items-center gap-3" data-id="element-983" data-mp="3cdwmexd2euah">
                <Button size="lg" onClick={onStart} data-id="element-984" data-mp="37e1xv0grigsl">
                  Join us
                  <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-985" data-mp="37g4xmc7pvoai" />
                </Button>
                <a href="#what-this-is" className="inline-flex min-h-tap items-center rounded-control px-1 text-small text-ink-muted underline-offset-4 hover:text-ink hover:underline" data-id="element-986" data-mp="3lb6yum1mcpgz">
                  What is this?
                </a>
              </div>
              <p className="text-small text-ink-muted" data-id="element-987" data-mp="8w63n3kqcs1i">
                Seven short questions, about three minutes. Nothing is required.
              </p>
            </div>

            <p className="flex items-start gap-2.5 rounded-field bg-canvas p-4 text-small text-ink-muted" data-id="element-988" data-mp="3ywgjuktcsgu">
              <LockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-989" data-mp="ngqwwgtm1u4g" />
              {from} knows they invited you and nothing more. They won&apos;t be told whether you
              joined, and you can ignore this without anyone knowing.
            </p>
          </div>

          <figure className="flex flex-col gap-3" data-id="element-990" data-mp="ilv77nbujiui">
            <div className="overflow-hidden rounded-card border border-rule" data-id="element-991" data-mp="3um7gle8ch3n">
              <img src={PANEL_PHOTOS.era.src} alt={PANEL_PHOTOS.era.alt} className="h-72 w-full object-cover lg:h-96" data-id="element-992" data-mp="8lcgq9ps143m" />
            </div>
            <figcaption className="text-small text-ink-muted" data-id="element-993" data-mp="3vyf0afirx7s8">
              {era.headline} {era.body}
            </figcaption>
          </figure>
        </div>
      </section>

      <section id="what-this-is" className="bg-card" data-id="element-994" data-mp="3h3qoykqozf4p">
        <div className="mx-auto grid w-full max-w-5xl gap-px overflow-hidden px-4 py-12 sm:px-6 lg:grid-cols-3" data-id="element-995" data-mp="3lzfud4pe7q8x">
          {[{
          title: 'An archive, indexed by year',
          body: '340 photographs, recordings, memories and records across fifty years. It opens on the years you were here.'
        }, {
          title: 'The people who overlapped you',
          body: 'Members appear as a first name and a last initial. You choose whether you appear at all.'
        }, {
          title: 'Nothing is public',
          body: 'It isn’t indexed by search engines, and your contact details are never shown to another member.'
        }].map(item => <div key={item.title} className="flex flex-col gap-1.5 border-t border-rule pt-5" data-id="element-996" data-mp="3rnmhk8tlt3c6">
              <h2 className="text-heading font-semibold text-ink" data-id="element-997" data-mp="3rpz4z1i4s5wm">{item.title}</h2>
              <p className="max-w-sm text-small text-ink-muted" data-id="element-998" data-mp="1ut9v6u5o9qo4">{item.body}</p>
            </div>)}
        </div>
      </section>
    </div>;
}